import React from 'react';
import { CompositeFilterDescriptor, FilterDescriptor, FilterOperator } from '@progress/kendo-data-query';
import { Chip } from '@progress/kendo-react-buttons';
import { useInternationalization } from '@progress/kendo-react-intl';
import { getOperatorLabel } from '../helpers/cpsGridHelpers';
import {
  useSearchContext,
  useGlobalizationContext,
  useCurrentUserContext,
  useGetAllFields
} from '@cs-cms/state-management';
import { CpsFieldForView, CPS_FIELD_TYPE, PERMISSIONS_FIELD, CPS_ACTIONS_ALLOWED_FOR_DOCUMENT } from '@cs-cms/types';

interface ActiveFilterPillsProps {
  compositeFilter?: CompositeFilterDescriptor;
  columns?: CpsFieldForView[];
  onRemoveFilter: (field: string, value?: string) => void;
}

interface FilterPill {
  field: string;
  displayName: string;
  value: string;
  originalValue: string;
  operator: FilterOperator;
  isDateRange?: boolean;
  isGlobalFilter?: boolean;
}

export const ActiveFilterPills: React.FC<ActiveFilterPillsProps> = ({ compositeFilter, columns, onRemoveFilter }) => {
  const intl = useInternationalization();
  const { queryRequest } = useSearchContext();
  const { selectedLocale } = useGlobalizationContext();
  const { selectedIndex } = useCurrentUserContext();
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);

  const formatValue = (value: any, fieldType: string): string => {
    const stringValue = value.toString();

    if (fieldType === CPS_FIELD_TYPE.BOOLEAN) {
      return value === true || value === 'true' ? 'Yes' : 'No';
    }

    if (
      fieldType === CPS_FIELD_TYPE.NUMERIC ||
      fieldType === CPS_FIELD_TYPE.INT32 ||
      fieldType === CPS_FIELD_TYPE.INT64
    ) {
      const numValue = parseFloat(stringValue);
      return !isNaN(numValue) ? intl.formatNumber(numValue, 'n2') : stringValue;
    }

    if (fieldType === CPS_FIELD_TYPE.DATE && (typeof value === 'string' || typeof value === 'number')) {
      return intl.formatDate(new Date(value));
    }

    if (fieldType === CPS_FIELD_TYPE.DATETIME && (typeof value === 'string' || typeof value === 'number')) {
      return intl.formatDate(new Date(value), { datetime: 'short' });
    }

    return stringValue;
  };

  const isDateRangeFilter = (filterDescriptors: FilterDescriptor[], fieldType: string): boolean => {
    if (filterDescriptors.length !== 2) {
      return false;
    }

    if (fieldType !== CPS_FIELD_TYPE.DATE && fieldType !== CPS_FIELD_TYPE.DATETIME) {
      return false;
    }

    const hasGte = filterDescriptors.some(f => f.operator === 'gte');
    const hasLte = filterDescriptors.some(f => f.operator === 'lte');

    return hasGte && hasLte;
  };

  const createDateRangePill = (
    filterDescriptors: FilterDescriptor[],
    fieldName: string,
    displayName: string,
    fieldType: string
  ): FilterPill | null => {
    const gteFilter = filterDescriptors.find(f => f.operator === 'gte');
    if (!gteFilter?.value) {
      return null;
    }

    const displayValue =
      fieldType === CPS_FIELD_TYPE.DATE
        ? intl.formatDate(new Date(gteFilter.value))
        : intl.formatDate(new Date(gteFilter.value), { datetime: 'short' });

    return {
      field: fieldName,
      displayName,
      value: displayValue,
      originalValue: gteFilter.value.toString(),
      operator: 'eq' as FilterOperator,
      isDateRange: true
    };
  };

  const createStandardPill = (
    filter: FilterDescriptor,
    fieldName: string,
    displayName: string,
    fieldType: string
  ): FilterPill => {
    const displayValue = formatValue(filter.value, fieldType);
    const originalValue = filter.value.toString();

    return {
      field: fieldName,
      displayName,
      value: displayValue,
      originalValue,
      operator: filter.operator as FilterOperator
    };
  };

  const extractGridFilterPills = (): FilterPill[] => {
    if (!compositeFilter || !columns) {
      return [];
    }

    const pills: FilterPill[] = [];
    const processedDateRanges = new Set<string>();

    compositeFilter.filters.forEach(descriptor => {
      const composite = descriptor as CompositeFilterDescriptor;
      if (composite.filters.length === 0) {
        return;
      }

      const filterDescriptors = composite.filters as FilterDescriptor[];
      const firstFilter = filterDescriptors[0];
      const fieldName = firstFilter.field as string;
      const operator = firstFilter.operator as FilterOperator;

      const column = columns.find(col => col.name === fieldName);
      const displayName = column?.displayName || fieldName;
      const fieldType = column?.type || CPS_FIELD_TYPE.TEXT;

      if (fieldName === PERMISSIONS_FIELD) {
        filterDescriptors.forEach(filter => {
          if (filter.value !== undefined && filter.value !== null) {
            pills.push({
              field: fieldName,
              displayName,
              value: filter.value.toString(),
              originalValue: filter.value.toString(),
              operator
            });
          }
        });
        return;
      }

      if (isDateRangeFilter(filterDescriptors, fieldType)) {
        const rangeKey = `${fieldName}-range`;
        if (!processedDateRanges.has(rangeKey)) {
          processedDateRanges.add(rangeKey);
          const pill = createDateRangePill(filterDescriptors, fieldName, displayName, fieldType);
          if (pill) {
            pills.push(pill);
          }
        }
        return;
      }

      filterDescriptors.forEach(filter => {
        if (filter.value === undefined || filter.value === null) {
          return;
        }
        pills.push(createStandardPill(filter, fieldName, displayName, fieldType));
      });
    });

    return pills;
  };

  const extractGlobalFilterPills = (): FilterPill[] => {
    if (!queryRequest?.filters) {
      return [];
    }

    const pills: FilterPill[] = [];

    queryRequest.filters
      .filter(filter => filter.isGlobal)
      .forEach(filter => {
        const fieldInfo = allFields?.find(f => f.name === filter.fieldName);
        const displayName = fieldInfo?.displayName || filter.fieldName;
        const fieldType = filter.fieldType;

        if (!filter.values || filter.values.length === 0) {
          return;
        }

        filter.values.forEach(value => {
          if (value === undefined || value === null) {
            return;
          }

          const displayValue = formatValue(value, fieldType);
          const originalValue = value.toString();

          pills.push({
            field: filter.fieldName,
            displayName,
            value: displayValue,
            originalValue,
            operator: filter.operator,
            isGlobalFilter: true
          });
        });
      });

    return pills;
  };

  const extractActionsFilterPills = (): FilterPill[] => {
    if (!queryRequest?.actionsFilter || queryRequest.actionsFilter.length === 0) {
      return [];
    }

    const pills: FilterPill[] = [];

    queryRequest.actionsFilter.forEach(actionValue => {
      let actionDisplayName = '';

      for (const key in CPS_ACTIONS_ALLOWED_FOR_DOCUMENT) {
        if (CPS_ACTIONS_ALLOWED_FOR_DOCUMENT[key as keyof typeof CPS_ACTIONS_ALLOWED_FOR_DOCUMENT] === actionValue) {
          const actionKey = key.charAt(0) + key.slice(1).toLowerCase();
          actionDisplayName = `Action: ${actionKey}`;
          break;
        }
      }

      pills.push({
        field: PERMISSIONS_FIELD,
        displayName: 'Actions',
        value: actionDisplayName,
        originalValue: actionValue.toString(),
        operator: 'eq' as FilterOperator,
        isGlobalFilter: true
      });
    });

    return pills;
  };

  const filterPills: FilterPill[] = [
    ...extractGridFilterPills(),
    ...extractGlobalFilterPills(),
    ...extractActionsFilterPills()
  ];

  if (filterPills.length === 0) {
    return null;
  }

  return (
    <div className="activeFilterPills">
      {filterPills.map((pill, index) => {
        const operatorLabel = getOperatorLabel(pill.operator);
        const chipLabel = operatorLabel
          ? `${pill.displayName} ${operatorLabel} ${pill.value}`
          : `${pill.displayName}: ${pill.value}`;

        return (
          <Chip
            key={`${pill.field}-${pill.originalValue}-${index}`}
            text={chipLabel}
            removable={true}
            onRemove={() => {
              if (pill.isDateRange) {
                onRemoveFilter(pill.field);
              } else {
                onRemoveFilter(pill.field, pill.originalValue);
              }
            }}
            size="small"
          />
        );
      })}
    </div>
  );
};
